// Which number is "this player's rating" — resolved from a ratings MAP keyed by
// rating list, rather than the three hardcoded FIDE fields it used to be.
//
// WHY A MAP
// A player used to carry exactly three ratings (standardRating / rapidRating /
// blitzRating) and the active tournament's `type` picked one. Arbiter-owned
// rating lists break that: a club can run any number of lists, each with its own
// id, and a tournament group binds to one of them. So the ratings live in
// `player.ratings`, keyed by list id, and 'standard' / 'rapid' / 'blitz' are just
// three well-known keys that happen to come from FIDE.
//
// BACKWARD COMPATIBILITY
// Every tournament saved before this change — and every import path that still
// writes them (TRF/TRX import, the FIDE search results, the Add Player form) —
// carries the flat `standardRating` fields and nothing else. readRatings() folds
// those in, so an old tournament resolves exactly as it always did and no
// migration is needed.
//
// Pure: no DOM, no state.js, no i18n. The human-readable tooltip is built by the
// caller (js/ui/players.js), which is where translation lives.

// The three FIDE ratings, in the order they are consulted when falling back.
// Order is load-bearing: the fallback picks the highest value and ties resolve to
// the earliest entry, which is the behaviour this replaced.
export const FIDE_RATING_KEYS = Object.freeze(['standard', 'rapid', 'blitz']);

/**
 * The one tournament `type` that is NOT a rating list.
 *
 * A children's and youth tournament sits in the same dropdown as standard,
 * rapid and blitz because that is where an arbiter looks for it, but it means
 * the opposite thing: nothing here is rated. Ratings are still shown and still
 * seed the pairing — a nine-year-old with 1400 is still stronger than one with
 * 900 — but the rating CALCULATION goes away, and with it the K-factor and the
 * birth year, which exist to feed that calculation and are a child's personal
 * data the rest of the time.
 */
export const YOUTH_TYPE = 'youth';

/** True when these settings describe a children's/youth tournament. */
export function isYouthTournament(settings) {
    return !!settings && settings.type === YOUTH_TYPE;
}

// Legacy flat field names, per well-known key. `standardRating` wins over the
// even older bare `standard`.
const LEGACY_FIELDS = Object.freeze({
    standard: ['standardRating', 'standard'],
    rapid: ['rapidRating', 'rapid'],
    blitz: ['blitzRating', 'blitz']
});

function toInt(v) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
}

// A rating list entry may be stored either as a bare number or as the richer
// { value, games, rd, vol } record a custom list keeps. Accept both.
function valueOf(entry) {
    if (entry === null || entry === undefined) return 0;
    if (typeof entry === 'object') return toInt(entry.value);
    return toInt(entry);
}

/**
 * Every rating this player has, keyed by rating list id, as plain integers.
 * Missing or unparseable values come back as 0 rather than being omitted for the
 * three FIDE keys, so callers can index them without guards.
 */
export function readRatings(player) {
    const p = player || {};
    const out = {};

    // Well-known FIDE keys first, from the legacy flat fields.
    FIDE_RATING_KEYS.forEach(key => {
        const fields = LEGACY_FIELDS[key];
        let raw = null;
        for (const f of fields) {
            if (p[f] !== undefined && p[f] !== null && p[f] !== '' && toInt(p[f]) !== 0) { raw = p[f]; break; }
        }
        out[key] = toInt(raw);
    });

    // The map wins where it has a value — it is the newer, authoritative shape.
    const map = p.ratings;
    if (map && typeof map === 'object') {
        Object.keys(map).forEach(key => {
            const v = valueOf(map[key]);
            if (v !== 0 || out[key] === undefined) out[key] = v;
        });
    }

    return out;
}

/** How many games this player has in a given list (0 when unknown). */
export function gamesInList(player, listId) {
    const map = player && player.ratings;
    if (!map || typeof map !== 'object') return 0;
    const entry = map[listId];
    if (!entry || typeof entry !== 'object') return 0;
    return toInt(entry.games);
}

/**
 * The best rating this player is known to hold, across every source they carry:
 * the three FIDE keys, any club list in their `ratings` map, and the manually
 * typed `player.rating`. 0 when they have none at all.
 *
 * This is the number to open with when a player arrives somewhere that has no
 * rating for them yet — a club database they are being added to. Taking the
 * highest rather than one nominated field means a player who is rated only for
 * rapid, or only on a club list, still enters at their real strength instead of
 * at the list's start rating.
 */
export function highestKnownRating(player) {
    const p = player || {};
    const ratings = readRatings(p);
    let best = 0;
    Object.keys(ratings).forEach(key => {
        if (ratings[key] > best) best = ratings[key];
    });
    // A starred value is a fallback resolveDisplayRating produced from this very
    // pool, so counting it would only re-find a number already counted.
    if (p.rating && !String(p.rating).includes('*')) {
        const manual = toInt(p.rating);
        if (manual > best) best = manual;
    }
    return best;
}

/**
 * The rating to show and seed with, for a player under a given set of settings.
 *
 * `settings.ratingListId` (a custom club list) takes precedence over
 * `settings.type` (standard/rapid/blitz). When the preferred list has no rating
 * for this player, fall back to the highest FIDE rating they do have — plus the
 * manually typed `player.rating` — and mark the result with a trailing '*'.
 *
 * @returns { rating, isFallback, source, display }
 *   `source` is the key the number actually came from ('standard', a list id, or
 *   'manual'); `display` is the string the UI renders.
 */
export function resolveDisplayRating(player, settings) {
    const p = player || {};
    const s = settings || {};
    // 'youth' names no rating list of its own, so it reads the standard rating.
    // Without this every player in a youth tournament would resolve through the
    // fallback path and be shown with a starred "borrowed rating" marker.
    const type = s.type === YOUTH_TYPE ? 'standard' : s.type;
    const preferredKey = s.ratingListId || type || 'standard';

    const ratings = readRatings(p);
    let rating = ratings[preferredKey] || 0;
    let source = preferredKey;
    let isFallback = false;

    if (!rating) {
        const pool = FIDE_RATING_KEYS.map(key => ({ val: ratings[key] || 0, type: key }));

        // The manually entered rating counts as a source too — unless it is
        // itself a starred fallback we produced earlier, which would make the
        // fallback self-referential.
        if (p.rating && !String(p.rating).includes('*')) {
            pool.push({ val: toInt(p.rating), type: 'manual' });
        }

        // Stable sort: ties keep FIDE_RATING_KEYS order, so standard beats an
        // equal rapid, exactly as before.
        const sorted = pool.slice().sort((a, b) => b.val - a.val);
        if (sorted[0] && sorted[0].val > 0) {
            rating = sorted[0].val;
            source = sorted[0].type;
            isFallback = source !== preferredKey;
        }
    }

    return {
        rating: rating || 0,
        isFallback,
        source,
        display: rating ? (rating + (isFallback ? '*' : '')) : '0'
    };
}
